import React, { useState, useEffect, useRef } from 'react';
import Modal from '../common/Modal';
import { ModalContent, ModalCloseButton, ConfirmationModal } from '../common';
import Button from '../common/Button';
import {
  FormSection,
  FormGrid,
  FormField,
  Input,
  Textarea,
  NumberInput,
  TimeInput,
  CurrencyInput,
  Select,
  Checkbox
} from '../common/Form';
import { Heading, Text } from '../common/Typography';
import { format, parseISO } from 'date-fns';
import { supabase } from '../../utils/supabase';
import {
  getSessionBlock,
  updateSessionBlock,
  updateBlockSessions,
  isBlockSessionDeletable,
  deleteUnplayedBlockSessions,
} from '../../services/blockBookingService';
import { resolveEndTimeForStartChange, getEndTimeMin } from '../../utils/sessionTimeUtils';
import {
  MATCH_FORMAT_DOUBLES,
  MATCH_FORMAT_SINGLES,
  doublesGameTypeRestrictionOptions,
  singlesGameTypeRestrictionOptions,
  singlesGameTypeRestrictionSelectValue,
} from '../../utils/competitiveMatchFormat';
import {
  DEFAULT_SCORING,
  getScoringRulesSummary,
  normalizeScoringConfig,
} from '../../utils/gameScoreRules';
import { isClubPlannableSessionType, isPegBoardSession } from '../../utils/sessionTypeUtils';
import {
  PEG_BOARD_QUEUE_RETURN_OPTIONS,
  PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
  PEG_BOARD_SELECTION_WINDOW_DEFAULT,
  PEG_BOARD_SELECTION_WINDOW_OPTIONS,
  clampPegBoardSelectionWindow,
  normalizePegBoardQueueReturn,
} from '../../utils/pegBoard/constants';
import SessionSeriesField from './SessionSeriesField';
import { seriesIdOrNull } from '../../utils/sessionSeries';
import { SIGNUP_SELECTION, isHostSelectSignup, normalizeSignupSelection } from '../../utils/signupSelection';

const UPDATABLE_STATUSES = ['upcoming', 'draft'];

const isUpdatableSession = (session, today) =>
  session.session_date >= today && UPDATABLE_STATUSES.includes(session.session_status);

const formatBlockSessionLabel = (session) => {
  let dateLabel = session.session_date || '';
  if (session.session_date) {
    try {
      dateLabel = format(parseISO(session.session_date), 'EEE, d MMM yyyy');
    } catch {
      dateLabel = session.session_date;
    }
  }
  const time = session.start_time ? String(session.start_time).substring(0, 5) : '';
  const name = session.name || 'Session';
  return [dateLabel, time, name].filter(Boolean).join(' · ');
};

/**
 * ManageBlockModal - Edit block metadata and apply session changes to all future sessions.
 */
const ManageBlockModal = ({ blockId, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [block, setBlock] = useState(null);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [blockName, setBlockName] = useState('');
  const [blockDescription, setBlockDescription] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [sessionDescription, setSessionDescription] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [maxPlayers, setMaxPlayers] = useState('16');
  const [numberOfCourts, setNumberOfCourts] = useState('1');
  const [location, setLocation] = useState('');
  const [pricePerPlayer, setPricePerPlayer] = useState('');
  const [currencyCode, setCurrencyCode] = useState('GBP');
  const [sessionTypeId, setSessionTypeId] = useState('');
  const [matchFormat, setMatchFormat] = useState(MATCH_FORMAT_DOUBLES);
  const [gameTypeRestriction, setGameTypeRestriction] = useState('all_types');
  const [enablePlayerCategorisation, setEnablePlayerCategorisation] = useState(false);
  const [pointsTarget, setPointsTarget] = useState(DEFAULT_SCORING.pointsTarget);
  const [deuceMode, setDeuceMode] = useState(DEFAULT_SCORING.deuceMode);
  const [pegBoardSelectionWindow, setPegBoardSelectionWindow] = useState(PEG_BOARD_SELECTION_WINDOW_DEFAULT);
  const [pegBoardQueueReturn, setPegBoardQueueReturn] = useState(PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST);
  const [sessionSeriesId, setSessionSeriesId] = useState('');
  const [signupSelection, setSignupSelection] = useState(SIGNUP_SELECTION.FIRST_COME);
  const [waitlistPromotionType, setWaitlistPromotionType] = useState('first_come_first_served');

  const initialRef = useRef(null);

  useEffect(() => {
    if (!blockId) return;
    let cancelled = false;
    const fetchBlock = async () => {
      setLoading(true);
      setError(null);
      const [result, typesResult] = await Promise.all([
        getSessionBlock(blockId),
        supabase.from('session_types').select('id, name').order('name'),
      ]);
      if (cancelled) return;
      if (typesResult.error) {
        console.error('Error fetching session types:', typesResult.error);
      }
      const allowedTypes = (typesResult.data || []).filter(isClubPlannableSessionType);
      setSessionTypes(allowedTypes);

      if (!result.success || !result.data) {
        setError(result.error || 'Failed to load block');
        setLoading(false);
        return;
      }
      const b = result.data;
      setBlock(b);
      const blockNameVal = b.block_name || '';
      const blockDescVal = b.description || '';
      setBlockName(blockNameVal);
      setBlockDescription(blockDescVal);
      const sessions = b.sessions || [];
      const today = new Date().toISOString().split('T')[0];
      const firstFuture =
        sessions.find((s) => isUpdatableSession(s, today)) ||
        sessions.find((s) => s.session_date >= today) ||
        sessions[0];
      let sessionNameVal = '';
      let sessionDescVal = '';
      let startTimeVal = '';
      let endTimeVal = '';
      let maxPlayersVal = '16';
      let numberOfCourtsVal = '1';
      let locationVal = '';
      let pricePerPlayerVal = '';
      let currencyCodeVal = 'GBP';
      let sessionTypeIdVal = '';
      let matchFormatVal = MATCH_FORMAT_DOUBLES;
      let gameTypeRestrictionVal = 'all_types';
      let enablePlayerCategorisationVal = false;
      let pointsTargetVal = DEFAULT_SCORING.pointsTarget;
      let deuceModeVal = DEFAULT_SCORING.deuceMode;
      let pegBoardSelectionWindowVal = PEG_BOARD_SELECTION_WINDOW_DEFAULT;
      let pegBoardQueueReturnVal = PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST;
      let sessionSeriesIdVal = '';
      let signupSelectionVal = SIGNUP_SELECTION.FIRST_COME;
      let waitlistPromotionTypeVal = 'first_come_first_served';
      if (firstFuture) {
        sessionNameVal = firstFuture.name || '';
        sessionDescVal = firstFuture.description || '';
        startTimeVal = firstFuture.start_time ? firstFuture.start_time.substring(0, 5) : '';
        endTimeVal = firstFuture.end_time ? firstFuture.end_time.substring(0, 5) : '';
        maxPlayersVal = String(firstFuture.max_players ?? 16);
        numberOfCourtsVal = String(firstFuture.number_of_courts ?? 1);
        locationVal = firstFuture.location || '';
        pricePerPlayerVal = firstFuture.price_per_player != null ? String(firstFuture.price_per_player) : '';
        currencyCodeVal = firstFuture.currency_code || 'GBP';
        sessionTypeIdVal = firstFuture.session_type_id || '';
        matchFormatVal = firstFuture.competitive_match_format || MATCH_FORMAT_DOUBLES;
        gameTypeRestrictionVal = firstFuture.game_type_restriction || 'all_types';
        enablePlayerCategorisationVal = Boolean(firstFuture.enable_player_categorisation);
        pointsTargetVal = firstFuture.points_target ?? DEFAULT_SCORING.pointsTarget;
        deuceModeVal = firstFuture.deuce_mode || DEFAULT_SCORING.deuceMode;
        pegBoardSelectionWindowVal = clampPegBoardSelectionWindow(firstFuture.peg_board_selection_window);
        pegBoardQueueReturnVal = normalizePegBoardQueueReturn(firstFuture.peg_board_queue_return);
        sessionSeriesIdVal = firstFuture.session_series_id || '';
        signupSelectionVal = normalizeSignupSelection(firstFuture.signup_selection);
        waitlistPromotionTypeVal = firstFuture.waitlist_promotion_type || 'first_come_first_served';
        setSessionName(sessionNameVal);
        setSessionDescription(sessionDescVal);
        setStartTime(startTimeVal);
        setEndTime(endTimeVal);
        setMaxPlayers(maxPlayersVal);
        setNumberOfCourts(numberOfCourtsVal);
        setLocation(locationVal);
        setPricePerPlayer(pricePerPlayerVal);
        setCurrencyCode(currencyCodeVal);
        setSessionTypeId(sessionTypeIdVal);
        setMatchFormat(matchFormatVal);
        setGameTypeRestriction(gameTypeRestrictionVal);
        setEnablePlayerCategorisation(enablePlayerCategorisationVal);
        setPointsTarget(pointsTargetVal);
        setDeuceMode(deuceModeVal);
        setPegBoardSelectionWindow(pegBoardSelectionWindowVal);
        setPegBoardQueueReturn(pegBoardQueueReturnVal);
        setSessionSeriesId(sessionSeriesIdVal);
        setSignupSelection(signupSelectionVal);
        setWaitlistPromotionType(waitlistPromotionTypeVal);
      }
      initialRef.current = {
        blockName: blockNameVal,
        blockDescription: blockDescVal,
        sessionName: sessionNameVal,
        sessionDescription: sessionDescVal,
        startTime: startTimeVal,
        endTime: endTimeVal,
        maxPlayers: maxPlayersVal,
        numberOfCourts: numberOfCourtsVal,
        location: locationVal,
        pricePerPlayer: pricePerPlayerVal,
        currencyCode: currencyCodeVal,
        sessionTypeId: sessionTypeIdVal,
        matchFormat: matchFormatVal,
        gameTypeRestriction: gameTypeRestrictionVal,
        enablePlayerCategorisation: enablePlayerCategorisationVal,
        pointsTarget: pointsTargetVal,
        deuceMode: deuceModeVal,
        pegBoardSelectionWindow: pegBoardSelectionWindowVal,
        pegBoardQueueReturn: pegBoardQueueReturnVal,
        sessionSeriesId: sessionSeriesIdVal,
        signupSelection: signupSelectionVal,
        waitlistPromotionType: waitlistPromotionTypeVal,
      };
      setLoading(false);
    };
    fetchBlock();
    return () => { cancelled = true; };
  }, [blockId]);

  const today = new Date().toISOString().split('T')[0];
  const allSessions = block?.sessions || [];
  const futureSessions = allSessions.filter((s) => isUpdatableSession(s, today));
  const pastCount = allSessions.filter((s) => s.session_date < today).length;
  const skippedFutureCount = allSessions.filter(
    (s) => s.session_date >= today && !UPDATABLE_STATUSES.includes(s.session_status)
  ).length;
  const deletableSessions = [...allSessions]
    .filter(isBlockSessionDeletable)
    .sort((a, b) => String(a.session_date || '').localeCompare(String(b.session_date || '')));
  const keptCount = allSessions.length - deletableSessions.length;

  const selectedSessionType = sessionTypes.find((t) => t.id === sessionTypeId);
  const isCompetitiveSession = selectedSessionType?.name === 'Competitive Session';
  const isPegBoard = isPegBoardSession(selectedSessionType);
  const scoringSummary = getScoringRulesSummary(
    normalizeScoringConfig({ points_target: pointsTarget, deuce_mode: deuceMode })
  );

  const handleMatchFormatChange = (next) => {
    setMatchFormat(next);
    if (next === MATCH_FORMAT_SINGLES) {
      setEnablePlayerCategorisation(false);
      setGameTypeRestriction((prev) => singlesGameTypeRestrictionSelectValue(prev));
    }
  };

  const handleSave = async () => {
    if (!blockId || !blockName.trim()) {
      setError('Block name is required');
      return;
    }
    if (futureSessions.length > 0 && !sessionTypeId) {
      setError('Session type is required');
      return;
    }
    const initial = initialRef.current;
    setSaving(true);
    setError(null);
    try {
      const blockUpdates = {};
      if (initial && blockName.trim() !== initial.blockName) {
        blockUpdates.block_name = blockName.trim();
      }
      if (initial && (blockDescription.trim() || '') !== (initial.blockDescription || '')) {
        blockUpdates.description = blockDescription.trim() || null;
      }
      if (Object.keys(blockUpdates).length > 0) {
        const blockResult = await updateSessionBlock(blockId, blockUpdates);
        if (!blockResult.success) throw new Error(blockResult.error);
      }

      const sessionUpdates = {};
      if (initial) {
        if ((sessionName.trim() || '') !== (initial.sessionName || '')) {
          sessionUpdates.name = sessionName.trim() || null;
        }
        if ((sessionDescription.trim() || '') !== (initial.sessionDescription || '')) {
          sessionUpdates.description = sessionDescription.trim() || null;
        }
        if ((startTime || '') !== (initial.startTime || '')) {
          sessionUpdates.start_time = startTime ? `${startTime}:00` : null;
        }
        if ((endTime || '') !== (initial.endTime || '')) {
          sessionUpdates.end_time = endTime ? `${endTime}:00` : null;
        }
        if ((maxPlayers || '') !== (initial.maxPlayers || '')) {
          sessionUpdates.max_players = maxPlayers ? parseInt(maxPlayers, 10) : null;
        }
        if ((numberOfCourts || '') !== (initial.numberOfCourts || '')) {
          sessionUpdates.number_of_courts = numberOfCourts ? parseInt(numberOfCourts, 10) : null;
        }
        if ((location.trim() || '') !== (initial.location || '')) {
          sessionUpdates.location = location.trim() || null;
        }
        if ((pricePerPlayer || '') !== (initial.pricePerPlayer || '')) {
          sessionUpdates.price_per_player = pricePerPlayer ? parseFloat(pricePerPlayer) : null;
        }
        if ((currencyCode || '') !== (initial.currencyCode || '')) {
          sessionUpdates.currency_code = currencyCode || null;
        }
        if ((sessionTypeId || '') !== (initial.sessionTypeId || '')) {
          sessionUpdates.session_type_id = sessionTypeId || null;
        }
        if (isCompetitiveSession) {
          if (matchFormat !== initial.matchFormat) {
            sessionUpdates.competitive_match_format = matchFormat;
          }
          if (gameTypeRestriction !== initial.gameTypeRestriction) {
            sessionUpdates.game_type_restriction = gameTypeRestriction;
          }
          if (enablePlayerCategorisation !== initial.enablePlayerCategorisation) {
            sessionUpdates.enable_player_categorisation = enablePlayerCategorisation;
          }
        }
        if (isCompetitiveSession || isPegBoard) {
          if (pointsTarget !== initial.pointsTarget) {
            sessionUpdates.points_target = pointsTarget;
          }
          if (deuceMode !== initial.deuceMode) {
            sessionUpdates.deuce_mode = deuceMode;
          }
        }
        if ((sessionSeriesId || '') !== (initial.sessionSeriesId || '')) {
          sessionUpdates.session_series_id = seriesIdOrNull(sessionSeriesId);
        }
        if (normalizeSignupSelection(signupSelection) !== normalizeSignupSelection(initial.signupSelection)) {
          sessionUpdates.signup_selection = normalizeSignupSelection(signupSelection);
          sessionUpdates.waitlist_enabled = true;
        }
        if (
          !isHostSelectSignup(signupSelection)
          && (waitlistPromotionType || 'first_come_first_served') !== (initial.waitlistPromotionType || 'first_come_first_served')
        ) {
          sessionUpdates.waitlist_promotion_type = waitlistPromotionType || 'first_come_first_served';
        }
        if (isPegBoard) {
          sessionUpdates.competitive_match_format = MATCH_FORMAT_DOUBLES;
          if (pegBoardSelectionWindow !== initial.pegBoardSelectionWindow) {
            sessionUpdates.peg_board_selection_window = clampPegBoardSelectionWindow(pegBoardSelectionWindow);
          }
          if (pegBoardQueueReturn !== initial.pegBoardQueueReturn) {
            sessionUpdates.peg_board_queue_return = normalizePegBoardQueueReturn(pegBoardQueueReturn);
          }
        }
      }
      if (Object.keys(sessionUpdates).length > 0) {
        const sessionsResult = await updateBlockSessions(blockId, sessionUpdates);
        if (!sessionsResult.success) throw new Error(sessionsResult.error);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update block');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlock = async () => {
    if (!blockId) return;
    setError(null);
    const result = await deleteUnplayedBlockSessions(blockId);
    if (!result.success) {
      setShowDeleteConfirm(false);
      setError(result.error || 'Failed to delete block');
      return;
    }
    setShowDeleteConfirm(false);
    onSuccess?.();
    onClose();
  };

  if (!blockId) return null;

  const sessionTypeOptions = sessionTypes.map((t) => ({ value: t.id, label: t.name }));

  return (
    <>
    <Modal isOpen={true} onClose={onClose}>
      <ModalContent
        size="medium"
        padding={false}
        className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden border border-border-medium shadow-xl"
      >
        <div className="relative flex shrink-0 items-center border-b border-border-medium px-4 py-4 pr-14 sm:px-6">
          <Heading level={2} className="text-text-primary">
            Manage block
          </Heading>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-6">
          {loading && (
            <div className="py-8 text-center text-text-secondary">Loading block...</div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-danger/30 bg-danger-light p-3">
              <Text className="text-sm text-danger-dark">{error}</Text>
            </div>
          )}

          {!loading && block && (
            <div className="space-y-4 sm:space-y-6">
              <div className="rounded-lg border border-border-medium bg-surface-subtle p-4">
                <Text className="text-sm text-text-secondary">
                  This block has <strong className="text-text-primary">{allSessions.length} sessions</strong>
                  {futureSessions.length > 0 && (
                    <>
                      ; <strong className="text-text-primary">{futureSessions.length} upcoming or draft</strong> session
                      {futureSessions.length !== 1 ? 's' : ''} will be updated when you save.
                    </>
                  )}
                  {futureSessions.length === 0 && (
                    <>
                      ; no upcoming or draft sessions will be updated.
                    </>
                  )}
                  {pastCount > 0 && <> Past sessions are not changed.</>}
                  {skippedFutureCount > 0 && (
                    <> Live, completed, or cancelled sessions are not changed.</>
                  )}
                </Text>
                <Text className="mt-2 text-xs text-text-muted">
                  Only fields you change here are applied to upcoming and draft sessions from today onward.
                </Text>
              </div>

              <FormSection title="Block information">
                <FormGrid columns={1}>
                  <FormField label="Block name" required>
                    <Input
                      value={blockName}
                      onChange={(e) => setBlockName(e.target.value)}
                      placeholder="e.g. Saturday Sessions - Spring 2026"
                    />
                  </FormField>
                  <FormField label="Description">
                    <Textarea
                      value={blockDescription}
                      onChange={(e) => setBlockDescription(e.target.value)}
                      placeholder="Optional description"
                      rows={2}
                    />
                  </FormField>
                </FormGrid>
              </FormSection>

              <FormSection
                title="Basic Information"
                description="Core details about sessions in this block"
              >
                <FormGrid columns={2}>
                  <FormField label="Session type" required>
                    <Select
                      value={sessionTypeId}
                      onChange={(e) => {
                        const nextId = e.target.value;
                        const nextType = sessionTypes.find((t) => t.id === nextId);
                        setSessionTypeId(nextId);
                        if (isPegBoardSession(nextType)) {
                          setMatchFormat(MATCH_FORMAT_DOUBLES);
                        }
                      }}
                      name="session_type_id"
                      placeholder="Select session type"
                      options={sessionTypeOptions}
                    />
                  </FormField>
                  <FormField label="Session name">
                    <Input
                      value={sessionName}
                      onChange={(e) => setSessionName(e.target.value)}
                      placeholder="e.g. Saturday Session"
                    />
                  </FormField>
                </FormGrid>
                <SessionSeriesField
                  clubId={block.club_id}
                  value={sessionSeriesId}
                  onChange={(nextId) => setSessionSeriesId(nextId || '')}
                />
                <FormField label="Description">
                  <Textarea
                    value={sessionDescription}
                    onChange={(e) => setSessionDescription(e.target.value)}
                    placeholder="Session description"
                    rows={2}
                  />
                </FormField>
              </FormSection>

              <FormSection title="Schedule" description="Session time">
                <FormGrid columns={2} className="[&>*]:min-w-0">
                  <TimeInput
                    label="Start time"
                    value={startTime}
                    onChange={(value) => {
                      setStartTime(value);
                      setEndTime((prev) => resolveEndTimeForStartChange(value, prev));
                    }}
                    name="start_time"
                    className="min-w-0 max-w-full"
                  />
                  <TimeInput
                    label="End time"
                    value={endTime}
                    onChange={setEndTime}
                    min={getEndTimeMin(startTime)}
                    name="end_time"
                    className="min-w-0 max-w-full"
                  />
                </FormGrid>
              </FormSection>

              <FormSection title="Session Details" description="Session capacity and venue details">
                <FormGrid columns={2} className="[&>*]:min-w-0">
                  <FormField label="Max players">
                    <NumberInput
                      name="max_players"
                      value={maxPlayers}
                      onChange={(e) => setMaxPlayers(e.target.value)}
                      allowEmpty
                      error={!maxPlayers || Number(maxPlayers) <= 0 ? 'Must be at least 1' : undefined}
                    />
                  </FormField>
                  <FormField label="Number of courts">
                    <NumberInput
                      name="number_of_courts"
                      value={numberOfCourts}
                      onChange={(e) => setNumberOfCourts(e.target.value)}
                      allowEmpty
                      error={!numberOfCourts || Number(numberOfCourts) <= 0 ? 'Must be at least 1' : undefined}
                    />
                  </FormField>
                </FormGrid>
                <FormField label="Location">
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. Main Hall"
                  />
                </FormField>
              </FormSection>

              <FormSection title="Capacity & Waitlist" description="Player capacity and waitlist management">
                <FormField
                  label="Who fills confirmed spots"
                  helperText={
                    isHostSelectSignup(signupSelection)
                      ? 'Everyone who signs up, including people you add or paste, goes onto the list. You choose who is confirmed.'
                      : 'Confirmed spots fill in signup order. Extra people go to the waitlist.'
                  }
                >
                  <Select
                    name="signup_selection"
                    value={normalizeSignupSelection(signupSelection)}
                    onChange={(e) => setSignupSelection(normalizeSignupSelection(e.target.value))}
                    options={[
                      { value: SIGNUP_SELECTION.FIRST_COME, label: 'First come, first served' },
                      { value: SIGNUP_SELECTION.HOST_SELECT, label: 'Host selects from the list' },
                    ]}
                  />
                </FormField>
                {!isHostSelectSignup(signupSelection) ? (
                  <FormField label="Waitlist Promotion Type">
                    <Select
                      name="waitlist_promotion_type"
                      value={waitlistPromotionType}
                      onChange={(e) => setWaitlistPromotionType(e.target.value)}
                      options={[
                        { value: 'first_come_first_served', label: 'First come, first served (automatic)' },
                        { value: 'manual_approval', label: 'Manual approval' },
                      ]}
                    />
                  </FormField>
                ) : null}
              </FormSection>

              <FormSection title="Pricing (optional)" description="Cost structure for the session">
                <FormGrid columns={2}>
                  <FormField label="Currency">
                    <Select
                      value={currencyCode}
                      onChange={(e) => setCurrencyCode(e.target.value)}
                      options={[
                        { value: 'GBP', label: '£ GBP' },
                        { value: 'USD', label: '$ USD' },
                        { value: 'EUR', label: '€ EUR' }
                      ]}
                    />
                  </FormField>
                  <FormField label="Price per player">
                    <CurrencyInput
                      name="price_per_player"
                      value={pricePerPlayer}
                      onChange={(e) => setPricePerPlayer(e.target.value)}
                      currency={currencyCode}
                    />
                  </FormField>
                </FormGrid>
              </FormSection>

              {isCompetitiveSession && (
                <>
                  <FormSection title="Matchmaking" description="Control how players are matched and grouped">
                    <FormField
                      label="Match format"
                      helperText="Singles uses one player per side (1v1). Doubles is the default (two per side)."
                    >
                      <Select
                        name="competitive_match_format"
                        value={matchFormat}
                        onChange={(e) => handleMatchFormatChange(e.target.value)}
                        options={[
                          { value: MATCH_FORMAT_DOUBLES, label: 'Doubles' },
                          { value: MATCH_FORMAT_SINGLES, label: 'Singles (1v1)' },
                        ]}
                      />
                    </FormField>
                    <FormField
                      label="Game Type Settings"
                      helperText={
                        matchFormat === MATCH_FORMAT_SINGLES
                          ? 'All types allows same-gender and cross-gender 1v1. Levels singles only is male vs male or female vs female.'
                          : 'Control which game types are allowed during matchmaking'
                      }
                    >
                      <Select
                        name="game_type_restriction"
                        value={
                          matchFormat === MATCH_FORMAT_SINGLES
                            ? singlesGameTypeRestrictionSelectValue(gameTypeRestriction)
                            : gameTypeRestriction
                        }
                        onChange={(e) => setGameTypeRestriction(e.target.value)}
                        options={
                          matchFormat === MATCH_FORMAT_SINGLES
                            ? singlesGameTypeRestrictionOptions
                            : doublesGameTypeRestrictionOptions
                        }
                      />
                    </FormField>
                    <FormField
                      label="Enable Player Categorisation"
                      helperText={
                        matchFormat === MATCH_FORMAT_SINGLES
                          ? 'Not used in singles sessions (tier matchmaking is for doubles).'
                          : 'Group players into skill tiers (A, B, C, D) based on ELO to prevent large skill gaps in matchmaking'
                      }
                    >
                      <Checkbox
                        name="enable_player_categorisation"
                        checked={enablePlayerCategorisation}
                        onChange={(e) => setEnablePlayerCategorisation(e.target.checked)}
                        disabled={matchFormat === MATCH_FORMAT_SINGLES}
                      />
                    </FormField>
                  </FormSection>

                  <FormSection
                    title="Game scoring"
                    description="Points target and deuce rules for score entry during the session"
                  >
                    <FormGrid columns={2}>
                      <FormField label="Points to win">
                        <Select
                          name="points_target"
                          value={String(pointsTarget)}
                          onChange={(e) => setPointsTarget(parseInt(e.target.value, 10))}
                          options={[
                            { value: '21', label: '21 points (current default)' },
                            { value: '15', label: '15 points (BWF 3×15)' },
                          ]}
                        />
                      </FormField>
                      <FormField label="Deuce">
                        <Select
                          name="deuce_mode"
                          value={deuceMode}
                          onChange={(e) => setDeuceMode(e.target.value)}
                          options={[
                            { value: 'standard', label: 'Standard (deuce + cap)' },
                            { value: 'no_deuce', label: 'No deuce (first to target wins)' },
                          ]}
                        />
                      </FormField>
                    </FormGrid>
                    <p className="text-sm text-text-secondary">{scoringSummary}</p>
                  </FormSection>
                </>
              )}

              {isPegBoard && (
                <>
                  <FormSection
                    title="Peg Board"
                    description="Queue-driven doubles. The picker at the front of the queue chooses a game; scores do not affect Elo."
                  >
                    <FormField
                      label="Picker can choose from"
                      helperText="Includes the picker. Must be at least 4 for doubles."
                    >
                      <Select
                        name="peg_board_selection_window"
                        value={String(clampPegBoardSelectionWindow(pegBoardSelectionWindow))}
                        onChange={(e) => setPegBoardSelectionWindow(parseInt(e.target.value, 10))}
                        options={PEG_BOARD_SELECTION_WINDOW_OPTIONS}
                      />
                    </FormField>
                    <FormField
                      label="Queue return order"
                      helperText="How the four players go back onto the queue after a score is entered"
                    >
                      <Select
                        name="peg_board_queue_return"
                        value={normalizePegBoardQueueReturn(pegBoardQueueReturn)}
                        onChange={(e) => setPegBoardQueueReturn(e.target.value)}
                        options={PEG_BOARD_QUEUE_RETURN_OPTIONS}
                      />
                    </FormField>
                  </FormSection>

                  <FormSection
                    title="Game scoring"
                    description="Points target and deuce rules for score entry during the session"
                  >
                    <FormGrid columns={2}>
                      <FormField label="Points to win">
                        <Select
                          name="points_target"
                          value={String(pointsTarget)}
                          onChange={(e) => setPointsTarget(parseInt(e.target.value, 10))}
                          options={[
                            { value: '21', label: '21 points (current default)' },
                            { value: '15', label: '15 points (BWF 3×15)' },
                          ]}
                        />
                      </FormField>
                      <FormField label="Deuce">
                        <Select
                          name="deuce_mode"
                          value={deuceMode}
                          onChange={(e) => setDeuceMode(e.target.value)}
                          options={[
                            { value: 'standard', label: 'Standard (deuce + cap)' },
                            { value: 'no_deuce', label: 'No deuce (first to target wins)' },
                          ]}
                        />
                      </FormField>
                    </FormGrid>
                    <p className="text-sm text-text-secondary">{scoringSummary}</p>
                  </FormSection>
                </>
              )}
            </div>
          )}
        </div>

        {!loading && block && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border-medium bg-surface-subtle px-4 py-4 sm:px-6">
            <Button
              variant="danger"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={saving}
            >
              Delete block
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save changes'}
              </Button>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
    <ConfirmationModal
      isOpen={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      onConfirm={handleDeleteBlock}
      title="Delete block booking"
      isDangerous
      confirmText={deletableSessions.length > 0 ? `Delete ${deletableSessions.length} session${deletableSessions.length !== 1 ? 's' : ''}` : 'Remove block'}
      cancelText="Keep block"
      message={(
        <div className="space-y-3">
          {deletableSessions.length > 0 ? (
            <>
              <p>
                These unplayed sessions will be deleted. Signups on them will be removed. This cannot be undone.
              </p>
              <ul className="max-h-60 overflow-y-auto rounded-md border border-border-medium bg-surface p-3 text-text-primary">
                {deletableSessions.map((session) => (
                  <li key={session.id} className="py-1">
                    {formatBlockSessionLabel(session)}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p>
              No unplayed sessions will be removed.
            </p>
          )}
          {keptCount > 0 && (
            <p>
              {keptCount} started or completed session{keptCount !== 1 ? 's' : ''} will be kept.
            </p>
          )}
        </div>
      )}
    />
    </>
  );
};

export default ManageBlockModal;
